import type { ReactElement } from "react";
import { GripVertical } from "lucide-react";
import type { TimelineMilestone } from "@playbookai/widget-schemas";
import { SortableItem } from "../../molecules/SortableList/SortableList.js";
import { Tooltip } from "../../molecules/index.js";
import { STATE_RING } from "../../lib/state-ring.js";
import { cn } from "../../lib/utils.js";
import { MilestoneDot } from "./MilestoneDot.js";
import { certaintyLabel, shortDate } from "./presentation.js";

export function MilestoneRow({
  milestone,
  readOnly,
  selected,
  onSelect,
}: {
  readonly milestone: TimelineMilestone;
  readonly readOnly: boolean;
  readonly selected: boolean;
  readonly onSelect: () => void;
}): ReactElement {
  const body = (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      aria-label={`Select milestone ${milestone.label}`}
      className="grid min-w-0 flex-1 grid-cols-[4.5rem_minmax(0,1fr)] items-start gap-x-3 gap-y-1 rounded px-2 py-3 text-start text-xs text-pb-text focus-ring sm:grid-cols-[4.5rem_minmax(0,1fr)_5rem]"
    >
      <span className="text-pb-text-muted">
        {shortDate(milestone.date) || "No date"}
      </span>
      <span className="flex min-w-0 items-start gap-2">
        <MilestoneDot milestone={milestone} size="md" className="mt-1" />
        <span className="min-w-0 break-words font-medium [overflow-wrap:anywhere]">
          {milestone.label || "Untitled milestone"}
        </span>
      </span>
      <span className="col-start-2 text-pb-text-muted sm:col-start-auto">
        {certaintyLabel(milestone)}
      </span>
    </button>
  );
  const rowClass = cn(
    "flex min-w-0 items-center px-2",
    selected && `bg-pb-teal/10 ${STATE_RING.teal}`,
  );
  if (readOnly) return <li className={rowClass}>{body}</li>;
  return (
    <SortableItem id={milestone.id}>
      {({ setNodeRef, style, handleProps, isDragging }) => (
        <li
          ref={setNodeRef}
          style={style}
          className={cn(
            rowClass,
            isDragging && "bg-pb-card-elevated shadow-md",
          )}
        >
          <Tooltip
            content="Press and hold to drag, or press Space then use the arrow keys"
            followAnchor
            wrapperClassName="shrink-0"
          >
            {/* eslint-disable-next-line ds/no-hand-built -- the sortable drag handle: dnd-kit's pointer and keyboard listeners, which no design-system Button carries */}
            <button
              type="button"
              aria-label={`Reorder ${milestone.label}`}
              className="shrink-0 cursor-grab rounded p-1 text-pb-text-muted hover:text-pb-text focus-ring active:cursor-grabbing"
              {...handleProps}
            >
              <GripVertical className="h-3.5 w-3.5" />
            </button>
          </Tooltip>
          {body}
        </li>
      )}
    </SortableItem>
  );
}
